import React, { useState } from 'react';
import { Outlet, Link, useLocation, useParams } from 'react-router-dom';
import { 
  LayoutGrid, 
  Mic2, 
  Sliders, 
  ChevronLeft, 
  ChevronRight, 
  FolderKanban, 
  Settings,
  LogOut
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export default function AppLayout() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const location = useLocation();
  const { brandSlug } = useParams();
  const { user, signOut } = useAuth();
  
  // Default slug for prototype navigation if not in URL
  const currentSlug = brandSlug || 'acme-robotics';

  // Helper to check active state
  const isActive = (path) => location.pathname.includes(path);

  return (
    <div className="flex h-screen bg-background text-white font-sans">
      {/* Collapsible Sidebar */}
      <aside 
        className={`${isCollapsed ? 'w-16' : 'w-64'} bg-surface border-r border-white/10 flex-shrink-0 flex flex-col transition-all duration-300 relative`}
      >
        {/* Toggle Button */}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 w-6 h-6 bg-surface border border-white/10 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-primary transition-colors z-50 shadow-lg"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>

        {/* Logo Area */}
        <div className="h-16 flex items-center justify-center border-b border-white/5">
          <Link to="/app/projects" className="flex items-center gap-2 overflow-hidden">
            <div className="w-8 h-8 bg-gradient-primary rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-white text-sm">
              P
            </div>
            {!isCollapsed && <span className="font-bold text-lg whitespace-nowrap">PodVault</span>}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-2 overflow-y-auto overflow-x-hidden">
           
           {/* Global Items */}
           <NavItem 
             to="/app/projects" 
             icon={<FolderKanban className="w-5 h-5" />} 
             label="Projects" 
             isCollapsed={isCollapsed} 
             active={isActive('/projects')}
           />

           {!isCollapsed && (
             <div className="pt-6 pb-2 pl-3 text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
               Brand Context
             </div>
           )}

           {/* Context-Aware Items (Would be dynamic in real app) */}
           <NavItem 
             to={`/app/brand/${currentSlug}/dashboard`} 
             icon={<LayoutGrid className="w-5 h-5" />} 
             label="Dashboard" 
             isCollapsed={isCollapsed} 
             active={isActive('/dashboard')}
           />
           <NavItem 
             to={`/app/brand/${currentSlug}/episodes`} 
             icon={<Mic2 className="w-5 h-5" />} 
             label="Episodes" 
             isCollapsed={isCollapsed} 
             active={isActive('/episodes')}
           />
           <NavItem 
             to={`/app/brand/${currentSlug}/settings`} 
             icon={<Sliders className="w-5 h-5" />} 
             label="Calibration" 
             isCollapsed={isCollapsed} 
             active={isActive('/settings')}
           />
        </nav>

        {/* Footer User Profile */}
        <div className="p-3 border-t border-white/5">
            <div className={`flex items-center justify-between p-2 rounded-lg hover:bg-white/5 transition-colors group cursor-default`}>
               <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} overflow-hidden w-full`}>
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0 border border-primary/30">
                    {user?.email?.[0].toUpperCase() || 'U'}
                  </div>
                  {!isCollapsed && (
                    <div className="overflow-hidden flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate" title={user?.email}>{user?.user_metadata?.full_name || 'User'}</div>
                      <div className="text-xs text-slate-500 truncate" title={user?.email}>{user?.email}</div>
                    </div>
                  )}
               </div>
               
               {!isCollapsed && (
                 <button 
                   onClick={signOut}
                   className="text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity p-1"
                   title="Log Out"
                 >
                   <LogOut className="w-4 h-4" />
                 </button>
               )}
            </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto relative">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({ to, icon, label, isCollapsed, active }) {
  return (
    <Link 
      to={to} 
      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all group ${
        active 
          ? 'bg-primary/10 text-primary' 
          : 'text-slate-400 hover:bg-white/5 hover:text-white'
      } ${isCollapsed ? 'justify-center' : ''}`}
      title={isCollapsed ? label : ''}
    >
      <div className={`${active ? 'text-primary' : 'group-hover:text-white'}`}>
        {icon}
      </div>
      {!isCollapsed && <span className="font-medium text-sm whitespace-nowrap">{label}</span>}
    </Link>
  );
}
